import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DataTable, type Column } from '../components/DataTable';
import { Button, ErrorBox, Field, Input, Modal, PageHeader, Pill, SearchInput, Select, Spinner } from '../components/ui';
import { fetchAll } from '../lib/fetchAll';
import { PROPERTY_TYPE_LABEL } from '../lib/format';
import { addressMatchKey, buildingKey, zip5 } from '../lib/import/normalize';
import { supabase, unwrap, type Tables } from '../lib/supabase';

async function loadBuildings() {
  return fetchAll((from, to) =>
    supabase
      .from('buildings')
      .select('id, name, street, city, state, zip5, property_type, address_raw, address_needs_review, org_locations(count), building_ownership(count)')
      .order('street')
      .range(from, to),
  );
}
type Row = Awaited<ReturnType<typeof loadBuildings>>[number];
const n = (x: { count: number }[] | null | undefined) => x?.[0]?.count ?? 0;

export function Buildings() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['buildings'], queryFn: loadBuildings });
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [special, setSpecial] = useState('');
  const [creating, setCreating] = useState(false);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((b) => {
      if (type && b.property_type !== type) return false;
      if (special === 'review' && !b.address_needs_review) return false;
      if (special === 'occupied' && n(b.org_locations) === 0) return false;
      if (special === 'owned' && n(b.building_ownership) === 0) return false;
      if (special === 'both' && (n(b.org_locations) === 0 || n(b.building_ownership) === 0)) return false;
      return !needle || [b.name, b.street, b.city, b.zip5, b.address_raw].some((s) => s?.toLowerCase().includes(needle));
    });
  }, [data, q, type, special]);

  const columns: Column<Row>[] = [
    {
      key: 'address',
      header: 'Building',
      sortValue: (b) => (b.name || b.street || b.address_raw || '').toLowerCase(),
      render: (b) => (
        <div>
          <span className="font-medium text-slate-900">{b.name || b.street || b.address_raw || '(no address)'}</span>
          {b.name && b.street && <span className="text-slate-500"> · {b.street}</span>}
          {b.address_needs_review && (
            <Pill color="orange" className="ml-2" title={b.address_raw ?? undefined}>
              check address
            </Pill>
          )}
        </div>
      ),
    },
    { key: 'city', header: 'City', sortValue: (b) => b.city?.toLowerCase(), render: (b) => [b.city, b.state].filter(Boolean).join(', ') },
    { key: 'zip', header: 'ZIP', sortValue: (b) => b.zip5, render: (b) => b.zip5 },
    { key: 'type', header: 'Type', sortValue: (b) => b.property_type, render: (b) => (b.property_type ? <Pill>{PROPERTY_TYPE_LABEL[b.property_type]}</Pill> : null) },
    { key: 'occ', header: 'Occupants', sortValue: (b) => n(b.org_locations), render: (b) => n(b.org_locations) || '', className: 'text-right' },
    { key: 'own', header: 'Owners', sortValue: (b) => n(b.building_ownership), render: (b) => n(b.building_ownership) || '', className: 'text-right' },
  ];

  return (
    <div>
      <PageHeader
        title="Buildings"
        subtitle={data ? `${rows.length} of ${data.length}` : undefined}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New building
          </Button>
        }
      />
      <div className="mb-3 flex flex-wrap gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="Filter by address, city, ZIP…" className="!w-64" />
        <Select value={type} onChange={(e) => setType(e.target.value)} className="!w-36">
          <option value="">All types</option>
          {Object.entries(PROPERTY_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <Select value={special} onChange={(e) => setSpecial(e.target.value)} className="!w-56">
          <option value="">All buildings</option>
          <option value="occupied">Has a known occupant</option>
          <option value="owned">Owner is a contact</option>
          <option value="both">Owner is a contact + has occupant</option>
          <option value="review">Address needs checking</option>
        </Select>
      </div>
      <ErrorBox error={error} />
      {isLoading ? <Spinner /> : <DataTable rows={rows} columns={columns} rowKey={(b) => b.id} onRowClick={(b) => navigate(`/buildings/${b.id}`)} initialSort={{ key: 'address', dir: 'asc' }} />}
      <Modal title="New building" open={creating} onClose={() => setCreating(false)}>
        <BuildingForm onDone={(id) => navigate(`/buildings/${id}`)} />
      </Modal>
    </div>
  );
}

export function BuildingForm({
  building,
  initialStreet,
  onDone,
}: {
  building?: Tables<'buildings'>;
  /** Pre-fills the street (e.g. what was typed into a search before choosing "Create"). */
  initialStreet?: string;
  onDone: (id: string, label: string) => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: building?.name ?? '',
    street: building?.street ?? initialStreet ?? '',
    city: building?.city ?? '',
    state: building?.state ?? 'GA',
    zip5: building?.zip5 ?? '',
    property_type: building?.property_type ?? '',
    notes: building?.notes ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: async () => {
      const zip = zip5(f.zip5);
      if (!f.street.trim() && !f.name.trim()) throw new Error('Enter a street address or a building name.');
      const row = {
        name: f.name.trim() || null,
        street: f.street.trim() || null,
        city: f.city.trim() || null,
        state: f.state.trim().toUpperCase() || null,
        zip5: zip,
        property_type: f.property_type || null,
        notes: f.notes.trim() || null,
        // Same key Practice Finder uses, so future imports land on this building.
        building_key: buildingKey(f.street, zip),
        address_match_key: addressMatchKey(f.street, zip ?? f.city),
        address_needs_review: !(f.street.trim() && f.city.trim() && zip),
      };
      const label = row.name || row.street || 'Building';
      if (building) {
        unwrap(await supabase.from('buildings').update(row).eq('id', building.id));
        return { id: building.id, label };
      }
      // Same street number + ZIP is the same building (Practice Finder's rule): reuse it, never duplicate.
      if (row.building_key) {
        const existing = unwrap(await supabase.from('buildings').select('id, name, street').eq('building_key', row.building_key).maybeSingle());
        if (existing) return { id: existing.id, label: existing.name || existing.street || label };
      }
      return { id: unwrap(await supabase.from('buildings').insert(row).select('id').single()).id, label };
    },
    onSuccess: ({ id, label }) => {
      qc.invalidateQueries({ queryKey: ['buildings'] });
      qc.invalidateQueries({ queryKey: ['building', id] });
      onDone(id, label);
    },
  });
  return (
    <div className="space-y-3">
      {building?.address_raw && <div className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">Originally entered as: “{building.address_raw}”</div>}
      <Field label="Building name (optional)">
        <Input value={f.name} onChange={set('name')} placeholder="e.g. Northside Doctors Centre" />
      </Field>
      <Field label="Street">
        <Input value={f.street} onChange={set('street')} autoFocus />
      </Field>
      <div className="grid grid-cols-6 gap-3">
        <Field label="City" className="col-span-3">
          <Input value={f.city} onChange={set('city')} />
        </Field>
        <Field label="State" className="col-span-1">
          <Input value={f.state} onChange={set('state')} />
        </Field>
        <Field label="ZIP" className="col-span-2">
          <Input value={f.zip5} onChange={set('zip5')} />
        </Field>
      </div>
      <Field label="Type">
        <Select value={f.property_type} onChange={set('property_type')}>
          <option value="">—</option>
          {Object.entries(PROPERTY_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Notes">
        <textarea rows={3} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" value={f.notes} onChange={set('notes')} />
      </Field>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          {building ? 'Save' : 'Create building'}
        </Button>
      </div>
    </div>
  );
}
